import type { Pt } from './edgeGeometry';

export type VertexSelection = {
  edgeId: string;
  kind?: 'dependency' | 'inheritance';
  index: number;
};

type RemoveVertexOptions = {
  minVertices?: number;
};

function clonePt(point: Pt): Pt {
  return { x: point.x, y: point.y };
}

function distanceToSegmentSquared(point: Pt, start: Pt, end: Pt): number {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;

  if (lengthSquared === 0) {
    const px = point.x - start.x;
    const py = point.y - start.y;
    return px * px + py * py;
  }

  const t = Math.max(
    0,
    Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared)
  );
  const projection = { x: start.x + t * dx, y: start.y + t * dy };
  const px = point.x - projection.x;
  const py = point.y - projection.y;
  return px * px + py * py;
}

export function insertVertexAtNearestSegment(
  vertices: readonly Pt[],
  points: readonly Pt[],
  point: Pt
): Pt[] {
  if (points.length < 2) return [...vertices.map(clonePt), clonePt(point)];

  let bestSegmentIndex = 0;
  let bestDistance = distanceToSegmentSquared(point, points[0], points[1]);

  for (let index = 1; index < points.length - 1; index++) {
    const distance = distanceToSegmentSquared(point, points[index], points[index + 1]);
    if (distance < bestDistance) {
      bestDistance = distance;
      bestSegmentIndex = index;
    }
  }

  const insertIndex = Math.min(bestSegmentIndex, vertices.length);
  const next = vertices.map(clonePt);
  next.splice(insertIndex, 0, clonePt(point));
  return next;
}

export type VertexInsertPlan = {
  edgeId: string;
  vertices: Pt[];
};

export type VertexInsertActions = {
  persistVertices: (edgeId: string, vertices: Pt[]) => void;
  selectEdge: (edgeId: string) => void;
};

/**
 * Bend handles are the only visual for a knikpunt that sits on an existing
 * segment. They render only when the edge is selected AND it has vertices.
 */
export function vertexHandlesVisible(input: {
  selected: boolean;
  vertices: readonly Pt[] | null | undefined;
}): boolean {
  return input.selected === true && input.vertices != null && input.vertices.length > 0;
}

export function planVertexInsert(input: {
  edgeId: string;
  existing: readonly Pt[];
  points: readonly Pt[];
  point: Pt;
}): VertexInsertPlan {
  return {
    edgeId: input.edgeId,
    vertices: insertVertexAtNearestSegment(input.existing, input.points, input.point),
  };
}

/**
 * Persist the new vertices and select the edge. Handles only render on the
 * selected edge, so skipping selection after a shift-click / double-click on an
 * unselected association or transition leaves the new knikpunt invisible.
 */
export function commitVertexInsert(plan: VertexInsertPlan, actions: VertexInsertActions): void {
  actions.persistVertices(plan.edgeId, plan.vertices);
  actions.selectEdge(plan.edgeId);
}

export const ADD_BEND_POINT_HINT = 'Shift+Click or double-click to add a bend point';
export const EDIT_BEND_POINT_HINT = 'Drag to move · Double-click or press Delete to remove';

/**
 * The bottom-left canvas hint. Hovering an edge explains how to add a knikpunt;
 * hovering a bend handle, or having one selected, explains move + remove (the
 * remove gestures are otherwise undiscoverable).
 */
export function vertexHintText(input: {
  edgeHover: boolean;
  vertexHover: boolean;
  vertexSelected: boolean;
}): string | null {
  if (input.vertexHover || input.vertexSelected) return EDIT_BEND_POINT_HINT;
  if (input.edgeHover) return ADD_BEND_POINT_HINT;
  return null;
}

/** Screen pixels a handle must travel before a pointer-down becomes a drag. */
export const VERTEX_DRAG_THRESHOLD_PX = 3;

/**
 * Whether pointer travel (SCREEN coords) is a drag. Below the threshold it is a
 * click: the bend must stay put instead of snapping to the cursor somewhere
 * inside the 22px hit circle.
 */
export function isVertexDrag(start: Pt, current: Pt): boolean {
  return Math.hypot(current.x - start.x, current.y - start.y) >= VERTEX_DRAG_THRESHOLD_PX;
}

export function moveVertex(vertices: readonly Pt[], index: number, point: Pt): Pt[] {
  return vertices.map((vertex, vertexIndex) => (vertexIndex === index ? clonePt(point) : clonePt(vertex)));
}

export function removeVertex(
  vertices: readonly Pt[],
  index: number,
  options: RemoveVertexOptions = {}
): Pt[] {
  if (options.minVertices != null && vertices.length <= options.minVertices) {
    return vertices.map(clonePt);
  }
  return vertices.filter((_, vertexIndex) => vertexIndex !== index).map(clonePt);
}

export function vertexSelectionEquals(
  a: VertexSelection | null,
  b: VertexSelection | null
): boolean {
  return (
    a != null &&
    b != null &&
    a.edgeId === b.edgeId &&
    a.kind === b.kind &&
    a.index === b.index
  );
}
